import { createRoute } from "@tanstack/react-router";
import { PLAN_PRICE_CENTS, PLAN_PRICE_CENTS_ANNUAL } from "@takelead/contracts";
import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { Check, ArrowRight } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PlanBadge,
  Button,
  Input,
  Select,
} from "@/components/ui";
import { useBillingStatus } from "@/hooks/useBillingStatus";
import { cn, toErrorDisplayMessage, toDisplayString, toSafeReactNode } from "@/lib/utils";
import { authenticatedRoute } from "../_authenticated";
import { useAuth } from "@/contexts/useAuth";
import { apiPost, trackEvent, getApiErrorMessage } from "@/api";
import { toast } from "@/lib/safe-toast";

interface PlanDef {
  id: string;
  name: string;
  price: { month: string; year: string };
  features: string[];
  popular?: boolean;
}

const PLAN_IDS = ["free", "starter", "pro", "team"] as const;

/**
 * Annual-discount percentage, derived from the contracts price table instead of
 * being baked into 28 hand-edited locale strings (AUDIT-I18N).
 *
 * The annual points are psychologically clean numbers rather than exactly 80 %
 * of 12× the monthly price, so the paid plans discount by 20.2–20.7 %. The
 * advertised figure is the SMALLEST of them, rounded down: a shown percentage
 * must never promise more than the cheapest plan actually saves.
 */
const ANNUAL_DISCOUNT_PERCENT = Math.floor(
  Math.min(
    ...PLAN_IDS.filter((id) => PLAN_PRICE_CENTS[id] > 0).map(
      (id) => (1 - PLAN_PRICE_CENTS_ANNUAL[id] / 12 / PLAN_PRICE_CENTS[id]) * 100
    )
  )
);

/** Ensures locale feature lists are always string[] for React (never raw nested objects). */
function planFeatureList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((f) => toDisplayString(f));
}

function BillingPage() {
  const { t, i18n } = useTranslation("billing");
  const { t: tCommon } = useTranslation("common");
  const { data: billing, isLoading, error } = useBillingStatus();
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const locale = i18n.language || "en";

  const PLANS: PlanDef[] = useMemo(
    () => [
      {
        id: "free",
        name: toDisplayString(t("plans.free.name")),
        price: {
          month: toDisplayString(t("plans.free.priceMonth")),
          year: toDisplayString(t("plans.free.priceYear")),
        },
        features: planFeatureList(t("plans.free.features", { returnObjects: true })),
        popular: false,
      },
      {
        id: "starter",
        name: toDisplayString(t("plans.starter.name")),
        price: {
          month: toDisplayString(t("plans.starter.priceMonth")),
          year: toDisplayString(t("plans.starter.priceYear")),
        },
        features: planFeatureList(t("plans.starter.features", { returnObjects: true })),
        popular: false,
      },
      {
        id: "pro",
        name: toDisplayString(t("plans.pro.name")),
        price: {
          month: toDisplayString(t("plans.pro.priceMonth")),
          year: toDisplayString(t("plans.pro.priceYear")),
        },
        features: planFeatureList(t("plans.pro.features", { returnObjects: true })),
        popular: true,
      },
      {
        id: "team",
        name: toDisplayString(t("plans.team.name")),
        price: {
          month: toDisplayString(t("plans.team.priceMonth")),
          year: toDisplayString(t("plans.team.priceYear")),
        },
        features: planFeatureList(t("plans.team.features", { returnObjects: true })),
        popular: false,
      },
    ],
    [t, i18n.language]
  );

  const CANCEL_REASONS = useMemo(
    () => [
      { value: "too_expensive", label: toDisplayString(t("cancelReasonTooExpensive")) },
      { value: "no_longer_needed", label: toDisplayString(t("cancelReasonNoLongerNeeded")) },
      { value: "missing_feature", label: toDisplayString(t("cancelReasonMissingFeature")) },
      { value: "technical_issue", label: toDisplayString(t("cancelReasonTechnicalIssue")) },
    ],
    [t, i18n.language]
  );

  const [interval, setInterval] = useState<"month" | "year">("month");
  const [checkingOut, setCheckingOut] = useState<string | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState("too_expensive");
  const [cancelDetail, setCancelDetail] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const formatPeriodEnd = (value: unknown): string => {
    const raw = toDisplayString(value, "");
    if (!raw) return "";
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString(toDisplayString(locale));
  };

  const toastedRef = useRef(false);
  const subscriptionStartedFiredRef = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("success") === "true") {
      if (!toastedRef.current) {
        toast.success(t("planUpdated"));
        window.history.replaceState({}, "", window.location.pathname);
        toastedRef.current = true;
      }
      if (token && !subscriptionStartedFiredRef.current) {
        subscriptionStartedFiredRef.current = true;
        trackEvent("subscription_started", token, {}).catch(() => {});
      }
    } else if (params.get("canceled") === "true" && !toastedRef.current) {
      toastedRef.current = true;
      toast(t("checkoutCanceled"));
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [token, t]);

  // The plan cards must agree with the rest of the app, which gates on the EFFECTIVE
  // entitlement (same authority as the nav badge via /usage). Falling back to `planId` keeps
  // an older API answer working.
  const currentPlanId = (billing?.effectivePlanId ?? billing?.planId ?? "free") as (typeof PLAN_IDS)[number];
  // Managing and cancelling apply to the subscription this user PERSONALLY holds — a team
  // member who inherits the plan has no row of their own to cancel.
  const ownPlanId = billing?.planId ?? "free";
  const hasActiveSub = ownPlanId !== "free" && billing?.status !== "none";

  const checkout = async (planId: string) => {
    if (!token) return;
    trackEvent("checkout_started", token, { plan_id: planId, interval }).catch(() => {});
    setCheckingOut(planId);
    try {
      const locale = typeof navigator !== "undefined" ? navigator.language : undefined;
      const res = await apiPost<{ url?: string }>("/billing/checkout", token, {
        planId,
        interval,
        successUrl: `${window.location.origin}/billing?success=true`,
        cancelUrl: `${window.location.origin}/billing?canceled=true`,
        ...(locale ? { locale } : {}),
      });
      if (res.ok && res.data.url) {
        window.location.href = res.data.url;
      } else if (!res.ok) {
        toast.error(getApiErrorMessage(res, toDisplayString(t("checkoutError"))));
      } else {
        toast.error(t("checkoutError"));
      }
    } catch (e) {
      toast.error(toErrorDisplayMessage(e) || t("checkoutError"));
    } finally {
      setCheckingOut(null);
    }
  };

  const openBillingPortal = async () => {
    if (!token) return;
    setOpeningPortal(true);
    try {
      const locale = typeof navigator !== "undefined" ? navigator.language : undefined;
      const res = await apiPost<{ url?: string }>("/billing/portal", token, locale ? { locale } : {});
      if (!res.ok) {
        toast.error(getApiErrorMessage(res, toDisplayString(t("portalError"))));
        return;
      }
      if (!res.data?.url) {
        toast.error(t("portalError"));
        return;
      }
      window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(toErrorDisplayMessage(e) || t("portalError"));
    } finally {
      setOpeningPortal(false);
    }
  };

  const submitCancelReason = async () => {
    if (!token) return;
    setCancelling(true);
    try {
      const res = await apiPost("/billing/cancel-reason", token, {
        reason: cancelReason,
        ...(cancelDetail.trim() ? { detail: cancelDetail.trim() } : {}),
      });
      if (!res.ok) {
        toast.error(getApiErrorMessage(res, toDisplayString(t("cancelReasonSubmitError"))));
        return;
      }
      // `reason_class`, not `reason`: the API's PII filter refuses `reason` outright, and the
      // classified form is exactly what this value is — one of the four fixed CANCEL_REASONS,
      // never the free-text `cancelDetail` (which goes to /billing/cancel-reason and stops
      // there).
      trackEvent("subscription_cancelled", token, { reason_class: cancelReason }).catch(() => {});
      await openBillingPortal();
      setShowCancelForm(false);
    } finally {
      setCancelling(false);
    }
  };

  const statusLabel =
    billing &&
    (billing.status === "active"
      ? toDisplayString(t("statusActive"))
      : billing.status === "past_due"
        ? toDisplayString(t("statusPastDue"))
        : billing.status === "canceled"
          ? toDisplayString(t("statusCanceled"))
          : toErrorDisplayMessage(billing.status));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("pageTitle"))}</h1>
        <p className="text-sm text-text-muted">{toSafeReactNode(t("pageDescription"))}</p>
      </div>

      {isLoading && (
        <p className="text-sm text-text-muted">{toSafeReactNode(tCommon("common.loading"))}</p>
      )}
      {error && (
        <p className="text-sm text-danger">{toErrorDisplayMessage(error)}</p>
      )}

      {billing && (
        <>
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div>
                <CardTitle className="text-2xl capitalize">
                  {toErrorDisplayMessage(currentPlanId)}
                </CardTitle>
                <p className="mt-1 text-sm text-text-muted">
                  {toSafeReactNode(t("status"))}:{" "}
                  <span
                    className={cn(
                      "rounded-md px-2 py-0.5 text-xs font-medium",
                      billing.status === "active"
                        ? "bg-success/20 text-success"
                        : billing.status === "canceled"
                          ? "bg-danger/20 text-danger"
                          : billing.status === "past_due"
                            ? "bg-amber-500/20 text-amber-500"
                            : "bg-surface-2 text-text-muted"
                    )}
                  >
                    {toSafeReactNode(statusLabel)}
                  </span>
                </p>
              </div>
              <PlanBadge planId={currentPlanId} />
            </CardHeader>
            <CardContent className="space-y-3">
              {formatPeriodEnd(billing.currentPeriodEnd) && (
                <p className="text-sm text-text-muted">
                  {toSafeReactNode(t("periodEnd"))}: {formatPeriodEnd(billing.currentPeriodEnd)}
                </p>
              )}
              {billing.status === "past_due" && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-400">
                  {toSafeReactNode(t("pastDueWarning"))}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button variant="primary" onClick={openBillingPortal} disabled={openingPortal}>
                  {openingPortal ? toSafeReactNode(t("opening")) : toSafeReactNode(t("manageButton"))}
                </Button>
                {hasActiveSub && (
                  <Button
                    variant="ghost"
                    onClick={() => setShowCancelForm(!showCancelForm)}
                  >
                    {toSafeReactNode(t("cancelSubscriptionButton"))}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {showCancelForm && (
            <Card>
              <CardHeader>
                <CardTitle>{toSafeReactNode(t("cancelFormTitle"))}</CardTitle>
                <CardDescription>{toSafeReactNode(t("cancelFormDescription"))}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                >
                  {CANCEL_REASONS.map((r) => (
                    <option key={toDisplayString(r.value)} value={toDisplayString(r.value)}>
                      {toSafeReactNode(r.label)}
                    </option>
                  ))}
                </Select>
                <Input
                  type="text"
                  placeholder={toDisplayString(t("detailsPlaceholder"))}
                  value={cancelDetail}
                  onChange={(e) => setCancelDetail(e.target.value)}
                />
                <div className="flex gap-2">
                  <Button
                    variant="danger"
                    disabled={cancelling}
                    onClick={submitCancelReason}
                  >
                    {cancelling ? toSafeReactNode(t("processing")) : toSafeReactNode(t("submitCancel"))}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setShowCancelForm(false)}
                  >
                    {toSafeReactNode(t("keepSubscription"))}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text">{toSafeReactNode(t("comparePlans"))}</h2>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-2 p-1">
            <button
              type="button"
              onClick={() => setInterval("month")}
              className={cn(
                "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                interval === "month"
                  ? "bg-accent text-white"
                  : "text-text-muted hover:text-text"
              )}
            >
              {toSafeReactNode(t("monthly"))}
            </button>
            <button
              type="button"
              onClick={() => setInterval("year")}
              className={cn(
                "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                interval === "year"
                  ? "bg-accent text-white"
                  : "text-text-muted hover:text-text"
              )}
            >
              {toSafeReactNode(t("annual", { percent: ANNUAL_DISCOUNT_PERCENT }))}
            </button>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((plan) => {
            const isCurrent = currentPlanId === plan.id;
            const isUpgrade =
              PLAN_IDS.indexOf(plan.id as (typeof PLAN_IDS)[number]) > PLAN_IDS.indexOf(currentPlanId as (typeof PLAN_IDS)[number]);

            return (
              <Card
                key={plan.id}
                className={cn(
                  "relative",
                  plan.popular && "ring-2 ring-accent"
                )}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-accent px-3 py-0.5 text-xs font-medium text-white">
                    {toSafeReactNode(t("popular"))}
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-lg">{toSafeReactNode(plan.name)}</CardTitle>
                  <p className="text-sm font-medium text-text">
                    {toSafeReactNode(plan.price[interval])}
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2">
                    {plan.features.map((f, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-text">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
                        {toSafeReactNode(f)}
                      </li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <Button variant="outline" size="sm" disabled className="w-full">
                      {toSafeReactNode(t("currentPlan"))}
                    </Button>
                  ) : isUpgrade && plan.id !== "free" ? (
                    <Button
                      variant="primary"
                      size="sm"
                      className="w-full"
                      disabled={checkingOut === plan.id}
                      onClick={() => checkout(plan.id)}
                    >
                      {checkingOut === plan.id ? "…" : <>{toSafeReactNode(t("upgrade"))} <ArrowRight className="ml-1 h-3 w-3" /></>}
                    </Button>
                  ) : plan.id !== "free" ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={openBillingPortal}
                    >
                      {toSafeReactNode(t("changePlan"))}
                    </Button>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export const billingRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "billing",
  component: BillingPage,
});
